/**
 * Voice Preferences Service
 * 
 * Manages user preferences for voice providers (STT and TTS).
 * Persists preferences to Firestore under users/{userId}/preferences/voice
 * 
 * Preferences are stored per-user and persist across sessions.
 */

import { doc, getDoc, setDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '../firebase';

/** @typedef {'web' | 'deepgram' | 'gemini_live'} SttProviderId */
/** @typedef {'openai' | 'deepgram' | 'gemini_flash_tts'} TtsProviderId */

/**
 * App ids for the per-app Gemini 3.5 Transcribe try-it overlay.
 * Independent of global `sttProvider` — default STT is unchanged when these are false.
 */
export const GEMINI35_TRANSCRIBE_APP_IDS = Object.freeze({
  CLINICAL_HUD2: 'clinicalHud2',
  CHARTMIND7: 'chartMind7',
  CLINICAL_HUD3: 'clinicalHud3',
  CHARTMIND: 'chartmind',
  CHART_MIND_I: 'chartMindI',
  CHART_MIND_I2: 'chartMindI2',
  VISIT_START: 'visitStart',
  CARE_START_PLUS: 'careStartPlus',
  SPECIALTY_KB: 'specialtyKb',
});

/** @typedef {typeof GEMINI35_TRANSCRIBE_APP_IDS[keyof typeof GEMINI35_TRANSCRIBE_APP_IDS]} Gemini35TranscribeAppId */

const GEMINI35_TRANSCRIBE_APP_ID_SET = new Set(
  Object.values(GEMINI35_TRANSCRIBE_APP_IDS)
);

/**
 * Normalize stored STT provider (migrates legacy `default` → `web`).
 * @param {string|undefined} raw
 * @returns {'web' | 'deepgram' | 'gemini_live'}
 */
export const normalizeSttProvider = (raw) => {
  if (raw === 'deepgram') return 'deepgram';
  if (raw === 'gemini_live') return 'gemini_live';
  return 'web';
};

const EARBUD_HINT_PAUSE_INTERVAL_DEFAULT_SECONDS = 3;
const EARBUD_HINT_PAUSE_INTERVAL_MIN_SECONDS = 2;
const EARBUD_HINT_PAUSE_INTERVAL_MAX_SECONDS = 10;
const PREFERRED_MIC_LABEL_FALLBACK_KEYWORDS = ['built-in', 'internal', 'default'];

// Default preferences
const DEFAULT_PREFERENCES = {
  // STT (single-language ChartMind path): browser Web Speech, Deepgram, or Gemini Live scribe
  sttProvider: 'web',
  /** STT when ChartMind translation mode is on (classic pipeline vs Gemini Live interpreter). */
  translationSttProvider: 'web',
  
  // TTS Provider: 'openai' | 'deepgram' (Aura) | 'gemini_flash_tts' (Gemini 3.1 Flash TTS preview)
  ttsProvider: 'openai',

  /**
   * Canonical OpenAI TTS voice (CareStart + Settings gear).
   * Legacy docs may still have `openaiSettings.ttsVoice` — migrated on read.
   */
  openai: {
    voice: 'nova',
  },

  /** Native audio voice when using Gemini Live (prebuilt voice name). */
  geminiLive: {
    voice: 'Aoede',
  },

  /**
   * VoxPont translator voice (prebuilt voice name). Intentionally separate from `geminiLive.voice`
   * so the VoxPont translator voice is independent of the ChartMind / SymptomCheck Gemini Live voice.
   */
  voxpont: {
    voice: 'Aoede',
  },
  earbudHintPauseInterval: EARBUD_HINT_PAUSE_INTERVAL_DEFAULT_SECONDS,
  preferredMicDeviceId: null,
  preferredMicDeviceLabel: null,
  
  // Deepgram-specific settings (when sttProvider is 'deepgram')
  deepgramSettings: {
    // STT model preference: 'nova3' (streaming), 'flux' (voice agent), 'whisper' (batch)
    sttModel: 'nova3',
    
    // TTS voice preference (Aura voice name)
    ttsVoice: 'asteria',
    
    // Enable code-switching for Nova-3 (en/es/fr)
    enableCodeSwitching: true,
    
    // Flux-specific settings (when using voice agent mode)
    flux: {
      eotThreshold: 0.7,       // 0.5-0.9, default 0.7
      eagerEotThreshold: 0.5,  // 0.3-0.9, must be set for EagerEndOfTurn
      eotTimeoutMs: 5000,      // 500-10000, default 5000
    },
  },

  /**
   * Per-app Gemini 3.5 Transcribe try-it overlay (default off).
   * Does not change global sttProvider. Missing keys mean false.
   * Keep this object in lockstep with GEMINI35_TRANSCRIBE_APP_IDS — ChartMind-I
   * and ChartMind-I2 were historically omitted here even though those surfaces
   * mount the overlay; normalizeAppsPrefs still fills them as false.
   */
  apps: {
    clinicalHud2: { gemini35Transcribe: false },
    chartMind7: { gemini35Transcribe: false },
    clinicalHud3: { gemini35Transcribe: false },
    chartmind: { gemini35Transcribe: false },
    chartMindI: { gemini35Transcribe: false },
    chartMindI2: { gemini35Transcribe: false },
    visitStart: { gemini35Transcribe: false },
    careStartPlus: { gemini35Transcribe: false },
    specialtyKb: { gemini35Transcribe: false },
  },
};

/**
 * @param {unknown} apps
 * @returns {Record<string, { gemini35Transcribe: boolean }>}
 */
function normalizeAppsPrefs(apps) {
  const out = { ...DEFAULT_PREFERENCES.apps };
  if (!apps || typeof apps !== 'object') return out;
  for (const appId of GEMINI35_TRANSCRIBE_APP_ID_SET) {
    const entry = apps[appId];
    out[appId] = {
      gemini35Transcribe: entry?.gemini35Transcribe === true,
    };
  }
  return out;
}

// Firestore path helper
const getPreferencesPath = (userId) => `users/${userId}/preferences/voice`;

/**
 * Get voice preferences for a user
 * @param {string} userId - Firebase user ID
 * @returns {Promise<Object>} User's voice preferences (merged with defaults)
 */
export const getVoicePreferences = async (userId) => {
  if (!userId) {
    console.warn('[VoicePreferences] No userId provided, returning defaults');
    return {
      ...DEFAULT_PREFERENCES,
      apps: normalizeAppsPrefs(DEFAULT_PREFERENCES.apps),
    };
  }

  try {
    const docRef = doc(db, getPreferencesPath(userId));
    const docSnap = await getDoc(docRef);

    if (docSnap.exists()) {
      const data = docSnap.data();
      const sttProvider = normalizeSttProvider(data.sttProvider);
      const translationSttProvider = normalizeSttProvider(
        data.translationSttProvider !== undefined && data.translationSttProvider !== null
          ? data.translationSttProvider
          : sttProvider
      );

      const openaiVoice =
        (typeof data.openai?.voice === 'string' && data.openai.voice.trim()) ||
        (typeof data.openaiSettings?.ttsVoice === 'string' &&
          data.openaiSettings.ttsVoice.trim()) ||
        DEFAULT_PREFERENCES.openai.voice;

      // Merge with defaults to ensure all fields exist
      return {
        ...DEFAULT_PREFERENCES,
        ...data,
        sttProvider,
        translationSttProvider,
        openai: {
          ...DEFAULT_PREFERENCES.openai,
          ...data.openai,
          voice: openaiVoice,
        },
        geminiLive: {
          ...DEFAULT_PREFERENCES.geminiLive,
          ...data.geminiLive,
        },
        voxpont: {
          ...DEFAULT_PREFERENCES.voxpont,
          ...data.voxpont,
        },
        deepgramSettings: {
          ...DEFAULT_PREFERENCES.deepgramSettings,
          ...data.deepgramSettings,
          flux: {
            ...DEFAULT_PREFERENCES.deepgramSettings.flux,
            ...data.deepgramSettings?.flux,
          },
        },
        apps: normalizeAppsPrefs(data.apps),
      };
    }

    return {
      ...DEFAULT_PREFERENCES,
      apps: normalizeAppsPrefs(DEFAULT_PREFERENCES.apps),
    };
  } catch (error) {
    console.error('[VoicePreferences] Error loading preferences:', error);
    return {
      ...DEFAULT_PREFERENCES,
      apps: normalizeAppsPrefs(DEFAULT_PREFERENCES.apps),
    };
  }
};

/**
 * Save voice preferences for a user
 * @param {string} userId - Firebase user ID
 * @param {Object} preferences - Preferences to save (partial or full)
 * @returns {Promise<boolean>} Success status
 */
export const saveVoicePreferences = async (userId, preferences) => {
  if (!userId) {
    console.error('[VoicePreferences] Cannot save without userId');
    return false;
  }

  try {
    // Get current preferences to merge
    const current = await getVoicePreferences(userId);
    
    // Merge preferences
    const merged = {
      ...current,
      ...preferences,
      openai: {
        ...current.openai,
        ...preferences.openai,
      },
      geminiLive: {
        ...current.geminiLive,
        ...preferences.geminiLive,
      },
      voxpont: {
        ...current.voxpont,
        ...preferences.voxpont,
      },
      deepgramSettings: {
        ...current.deepgramSettings,
        ...preferences.deepgramSettings,
        flux: {
          ...current.deepgramSettings?.flux,
          ...preferences.deepgramSettings?.flux,
        },
      },
      apps: normalizeAppsPrefs({
        ...current.apps,
        ...preferences.apps,
        ...(preferences.apps
          ? Object.fromEntries(
              Object.entries(preferences.apps).map(([appId, entry]) => [
                appId,
                {
                  ...(current.apps?.[appId] || {}),
                  ...(entry || {}),
                },
              ])
            )
          : {}),
      }),
      updatedAt: serverTimestamp(),
    };

    const docRef = doc(db, getPreferencesPath(userId));
    await setDoc(docRef, merged, { merge: true });

    return true;
    
  } catch (error) {
    console.error('[VoicePreferences] Error saving preferences:', error);
    return false;
  }
};

/**
 * Set STT provider preference
 * @param {string} userId - Firebase user ID
 * @param {SttProviderId | 'default'} provider - `default` is accepted and stored as `web`
 * @returns {Promise<boolean>} Success status
 */
export const setSTTProvider = async (userId, provider) => {
  const normalized = provider === 'default' ? 'web' : normalizeSttProvider(provider);
  if (!['web', 'deepgram', 'gemini_live'].includes(normalized)) {
    console.error('[VoicePreferences] Invalid STT provider:', provider);
    return false;
  }
  return saveVoicePreferences(userId, { sttProvider: normalized });
};

/**
 * Set STT provider for ChartMind translation mode (independent of {@link sttProvider}).
 */
export const setTranslationSTTProvider = async (userId, provider) => {
  const normalized = provider === 'default' ? 'web' : normalizeSttProvider(provider);
  if (!['web', 'deepgram', 'gemini_live'].includes(normalized)) {
    console.error('[VoicePreferences] Invalid translation STT provider:', provider);
    return false;
  }
  return saveVoicePreferences(userId, { translationSttProvider: normalized });
};

/**
 * Set TTS provider preference
 * @param {string} userId - Firebase user ID
 * @param {TtsProviderId} provider - Provider to use (`openai` | `deepgram` | `gemini_flash_tts`)
 * @returns {Promise<boolean>} Success status
 */
export const setTTSProvider = async (userId, provider) => {
  if (!['deepgram', 'openai', 'gemini_flash_tts'].includes(provider)) {
    console.error('[VoicePreferences] Invalid TTS provider:', provider);
    return false;
  }
  return saveVoicePreferences(userId, { ttsProvider: provider });
};

/**
 * Update Deepgram-specific settings
 * @param {string} userId - Firebase user ID
 * @param {Object} settings - Deepgram settings to update
 * @returns {Promise<boolean>} Success status
 */
export const updateDeepgramSettings = async (userId, settings) => {
  return saveVoicePreferences(userId, {
    deepgramSettings: settings,
  });
};

/**
 * Get user's earbud hint pause interval (seconds)
 * @param {string} userId - Firebase user ID
 * @returns {Promise<number>} Pause interval in seconds (default 3)
 */
export const getEarbudHintPauseInterval = async (userId) => {
  const preferences = await getVoicePreferences(userId);
  const rawValue = Number(preferences?.earbudHintPauseInterval);
  if (!Number.isFinite(rawValue)) {
    return EARBUD_HINT_PAUSE_INTERVAL_DEFAULT_SECONDS;
  }
  return Math.max(
    EARBUD_HINT_PAUSE_INTERVAL_MIN_SECONDS,
    Math.min(EARBUD_HINT_PAUSE_INTERVAL_MAX_SECONDS, rawValue)
  );
};

/**
 * Set user's earbud hint pause interval (seconds)
 * @param {string} userId - Firebase user ID
 * @param {number} seconds - Requested pause interval
 * @returns {Promise<boolean>} Success status
 */
export const setEarbudHintPauseInterval = async (userId, seconds) => {
  if (!Number.isFinite(seconds)) {
    console.error('[VoicePreferences] Invalid earbud hint pause interval:', seconds);
    return false;
  }

  const clampedSeconds = Math.max(
    EARBUD_HINT_PAUSE_INTERVAL_MIN_SECONDS,
    Math.min(EARBUD_HINT_PAUSE_INTERVAL_MAX_SECONDS, seconds)
  );

  return saveVoicePreferences(userId, { earbudHintPauseInterval: clampedSeconds });
};

/**
 * Get preferred microphone metadata for a user.
 * @param {string} userId - Firebase user ID
 * @returns {Promise<{deviceId: string | null, label: string | null}>}
 */
export const getPreferredMic = async (userId) => {
  const preferences = await getVoicePreferences(userId);
  const storedDeviceId = preferences?.preferredMicDeviceId;
  const storedLabel = preferences?.preferredMicDeviceLabel;

  return {
    deviceId:
      typeof storedDeviceId === 'string' && storedDeviceId.trim().length > 0
        ? storedDeviceId
        : null,
    label:
      typeof storedLabel === 'string' && storedLabel.trim().length > 0
        ? storedLabel
        : null,
  };
};

/**
 * Save preferred microphone metadata for a user.
 * @param {string} userId - Firebase user ID
 * @param {{deviceId: string, label?: string|null}} preferredMic
 * @returns {Promise<boolean>} Success status
 */
export const setPreferredMic = async (userId, preferredMic = {}) => {
  const trimmedDeviceId =
    typeof preferredMic?.deviceId === 'string' ? preferredMic.deviceId.trim() : '';
  if (!trimmedDeviceId) {
    return false;
  }

  const normalizedLabel =
    typeof preferredMic?.label === 'string' && preferredMic.label.trim().length > 0
      ? preferredMic.label
      : null;

  return saveVoicePreferences(userId, {
    preferredMicDeviceId: trimmedDeviceId,
    preferredMicDeviceLabel: normalizedLabel,
  });
};

/**
 * Resolve the best microphone device id based on stored preferences and available devices.
 * @param {MediaDeviceInfo[]} availableDevices
 * @param {string|null|undefined} preferredMicDeviceId
 * @param {string|null|undefined} preferredMicDeviceLabel
 * @returns {string|null}
 */
export const resolvePreferredMicDeviceId = (
  availableDevices,
  preferredMicDeviceId,
  preferredMicDeviceLabel
) => {
  const devices = Array.isArray(availableDevices)
    ? availableDevices.filter((device) => device && typeof device.deviceId === 'string')
    : [];
  if (!devices.length) return null;

  const trimmedPreferredId =
    typeof preferredMicDeviceId === 'string' ? preferredMicDeviceId.trim() : '';
  if (trimmedPreferredId) {
    const exactIdMatch = devices.find((device) => device.deviceId === trimmedPreferredId);
    if (exactIdMatch) return exactIdMatch.deviceId;
  }

  const normalizedPreferredLabel =
    typeof preferredMicDeviceLabel === 'string' ? preferredMicDeviceLabel.trim().toLowerCase() : '';
  if (normalizedPreferredLabel) {
    const labelMatch = devices.find((device) =>
      (device.label || '').toLowerCase().includes(normalizedPreferredLabel)
    );
    if (labelMatch?.deviceId) return labelMatch.deviceId;
  }

  const builtInMatch = devices.find((device) => {
    const normalizedLabel = (device.label || '').toLowerCase();
    return PREFERRED_MIC_LABEL_FALLBACK_KEYWORDS.some((keyword) =>
      normalizedLabel.includes(keyword)
    );
  });
  if (builtInMatch?.deviceId) return builtInMatch.deviceId;

  return null;
};

/**
 * Reset preferences to defaults
 * @param {string} userId - Firebase user ID
 * @returns {Promise<boolean>} Success status
 */
export const resetVoicePreferences = async (userId) => {
  if (!userId) {
    console.error('[VoicePreferences] Cannot reset without userId');
    return false;
  }

  try {
    const docRef = doc(db, getPreferencesPath(userId));
    await setDoc(docRef, {
      ...DEFAULT_PREFERENCES,
      updatedAt: serverTimestamp(),
    });

    return true;
    
  } catch (error) {
    console.error('[VoicePreferences] Error resetting preferences:', error);
    return false;
  }
};

/**
 * Whether the per-app Gemini 3.5 Transcribe overlay is on (prefs object; no network).
 * @param {Object|null|undefined} preferences
 * @param {string} appId
 * @returns {boolean}
 */
export const isGemini35TranscribeEnabled = (preferences, appId) => {
  if (!GEMINI35_TRANSCRIBE_APP_ID_SET.has(appId)) return false;
  return preferences?.apps?.[appId]?.gemini35Transcribe === true;
};

/**
 * Read the per-app Gemini 3.5 Transcribe try-it flag (default false).
 * @param {string} userId
 * @param {string} appId
 * @returns {Promise<boolean>}
 */
export const getAppGemini35Transcribe = async (userId, appId) => {
  if (!userId || !GEMINI35_TRANSCRIBE_APP_ID_SET.has(appId)) return false;
  const preferences = await getVoicePreferences(userId);
  return isGemini35TranscribeEnabled(preferences, appId);
};

/**
 * Set the per-app Gemini 3.5 Transcribe try-it flag.
 * @param {string} userId
 * @param {string} appId
 * @param {boolean} enabled
 * @returns {Promise<boolean>}
 */
export const setAppGemini35Transcribe = async (userId, appId, enabled) => {
  if (!userId || !GEMINI35_TRANSCRIBE_APP_ID_SET.has(appId)) {
    console.error('[VoicePreferences] Invalid Gemini 3.5 Transcribe app id:', appId);
    return false;
  }
  return saveVoicePreferences(userId, {
    apps: {
      [appId]: { gemini35Transcribe: enabled === true },
    },
  });
};

/**
 * Check if Deepgram is enabled for STT
 * @param {Object} preferences - User preferences object
 * @returns {boolean}
 */
export const isDeepgramSTTEnabled = (preferences) => {
  return normalizeSttProvider(preferences?.sttProvider) === 'deepgram';
};

/** Browser Web Speech API path (+ app Whisper batch where applicable), not Deepgram or Gemini Live */
export const isWebSpeechSttProvider = (preferences) => {
  return normalizeSttProvider(preferences?.sttProvider) === 'web';
};

/** User selected Gemini Live for STT; ChartMind implements sessions (other flows fall back to web/Deepgram rules in sttFactory). */
export const isGeminiLiveSttProvider = (preferences) => {
  return preferences?.sttProvider === 'gemini_live';
};

/**
 * Check if Deepgram is enabled for TTS
 * @param {Object} preferences - User preferences object
 * @returns {boolean}
 */
export const isDeepgramTTSEnabled = (preferences) => {
  return preferences?.ttsProvider === 'deepgram';
};

/**
 * CareStart Settings gear / interview TTS: OpenAI, Gemini Flash, or Deepgram Aura.
 * @param {string|undefined} raw
 * @returns {'openai' | 'gemini_flash_tts' | 'deepgram'}
 */
export const normalizeCareStartTtsProvider = (raw) => {
  if (raw === 'gemini_flash_tts') return 'gemini_flash_tts';
  if (raw === 'deepgram') return 'deepgram';
  return 'openai';
};

/**
 * Resolve OpenAI TTS voice from prefs (canonical `openai.voice` + legacy keys).
 * @param {Object} [preferences]
 * @param {string} [fallback='nova']
 * @returns {string}
 */
export const resolveOpenAiTtsVoice = (preferences, fallback = 'nova') => {
  const fromCanonical =
    typeof preferences?.openai?.voice === 'string'
      ? preferences.openai.voice.trim()
      : '';
  if (fromCanonical) return fromCanonical;
  const fromLegacy =
    typeof preferences?.openaiSettings?.ttsVoice === 'string'
      ? preferences.openaiSettings.ttsVoice.trim()
      : '';
  if (fromLegacy) return fromLegacy;
  return fallback;
};

/**
 * Resolve Gemini Live / Flash prebuilt voice name.
 * @param {Object} [preferences]
 * @param {string} [fallback='Aoede']
 * @returns {string}
 */
export const resolveGeminiLiveVoice = (preferences, fallback = 'Aoede') => {
  const voice =
    typeof preferences?.geminiLive?.voice === 'string'
      ? preferences.geminiLive.voice.trim()
      : '';
  return voice || fallback;
};

/**
 * Resolve Deepgram Aura TTS voice from prefs.
 * @param {Object} [preferences]
 * @param {string} [fallback='asteria']
 * @returns {string}
 */
export const resolveDeepgramTtsVoice = (preferences, fallback = 'asteria') => {
  const fromSettings =
    typeof preferences?.deepgramSettings?.ttsVoice === 'string'
      ? preferences.deepgramSettings.ttsVoice.trim()
      : '';
  if (fromSettings) return fromSettings;
  const fromLegacy =
    typeof preferences?.deepgram?.voice === 'string'
      ? preferences.deepgram.voice.trim()
      : '';
  if (fromLegacy) return fromLegacy;
  return fallback;
};

/**
 * Snapshot used by CareStart interview + Settings gear.
 * @param {Object} [preferences]
 * @returns {{
 *   ttsProvider: 'openai' | 'gemini_flash_tts' | 'deepgram',
 *   openaiVoice: string,
 *   geminiLiveVoice: string,
 *   deepgramVoice: string,
 * }}
 */
export const resolveCareStartTtsSettings = (preferences) => ({
  ttsProvider: normalizeCareStartTtsProvider(preferences?.ttsProvider),
  openaiVoice: resolveOpenAiTtsVoice(preferences, 'nova'),
  geminiLiveVoice: resolveGeminiLiveVoice(preferences, 'Aoede'),
  deepgramVoice: resolveDeepgramTtsVoice(preferences, 'asteria'),
});

/**
 * Get the appropriate Deepgram STT mode based on context
 * @param {Object} preferences - User preferences object
 * @param {Object} context - { isVoiceAgent: boolean, language: string }
 * @returns {'nova3' | 'flux' | 'whisper'}
 */
export const getDeepgramSTTMode = (preferences, context = {}) => {
  const { isVoiceAgent = false, language = 'en-US' } = context;
  
  // Voice agent mode always uses Flux
  if (isVoiceAgent) {
    return 'flux';
  }
  
  // Swahili requires Whisper Cloud (batch)
  if (language.startsWith('sw')) {
    return 'whisper';
  }
  
  // Use user's preference or default to Nova-3
  return preferences?.deepgramSettings?.sttModel || 'nova3';
};

// Export service as default object for convenience
const voicePreferencesService = {
  getVoicePreferences,
  saveVoicePreferences,
  setSTTProvider,
  setTranslationSTTProvider,
  setTTSProvider,
  updateDeepgramSettings,
  getEarbudHintPauseInterval,
  setEarbudHintPauseInterval,
  getPreferredMic,
  setPreferredMic,
  resolvePreferredMicDeviceId,
  resetVoicePreferences,
  normalizeSttProvider,
  isDeepgramSTTEnabled,
  isWebSpeechSttProvider,
  isGeminiLiveSttProvider,
  isDeepgramTTSEnabled,
  normalizeCareStartTtsProvider,
  resolveOpenAiTtsVoice,
  resolveGeminiLiveVoice,
  resolveDeepgramTtsVoice,
  resolveCareStartTtsSettings,
  getDeepgramSTTMode,
  isGemini35TranscribeEnabled,
  getAppGemini35Transcribe,
  setAppGemini35Transcribe,
  GEMINI35_TRANSCRIBE_APP_IDS,
  DEFAULT_PREFERENCES,
};

export default voicePreferencesService;
